import React from "react";

interface ModalProps {
  visible: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  position?: "center" | "bottom";
  fullScreen?: boolean;
  /**
   * Replaces the default classes on the dialog wrapper.
   *
   * The centred default caps the panel at `max-w-md` (28rem). A dialog that
   * declares its own width is wider than that and clips its own content, so
   * such callers pass their width here instead of inheriting a cap they already
   * override. Omitting it keeps the historical `mx-4 w-full max-w-md` (centred)
   * and `w-full` (bottom sheet) classes unchanged.
   */
  panelClassName?: string;
}

/**
 * Generic overlay modal. `position="bottom"` renders a bottom-sheet style
 * container (used by filter / reject modals), `position="center"` renders a
 * centered dialog. `fullScreen` renders a full-viewport overlay (PDF viewer).
 */
const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  children,
  position = "center",
  fullScreen = false,
  panelClassName,
}) => {
  if (!visible) return null;
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
        <div className="h-full w-full" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      </div>
    );
  }
  return (
    <div
      className={`fixed inset-0 z-50 bg-black/50 ${
        position === "bottom" ? "flex items-end" : "flex items-center justify-center"
      }`}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={
          panelClassName ??
          (position === "bottom" ? "w-full" : "mx-4 w-full max-w-md")
        }
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;